export interface RevisionEntry {
  id: number;
  historialId?: number;
  elementoTipo: string;
  elementoId: string | null;
  elementoNombre?: string | null;
  estado: string;
  observacion?: string | null;
  adminNombre?: string | null;
  fecha: string;
  ronda: number;
  /** Archivo de la nueva versión, cuando esta entrada es un evento de reemplazo
   * (SISTEMA_EVIDENCIA_SUBIDA / SISTEMA_AUDIO_VINCULADO), o el archivo original (sintético).
   * `url: null` = solo tag, sin acceso al archivo (versión ya superada, el asesor la "olvida").
   * `eliminable` marca la versión vigente como borrable desde el propio chat. */
  archivoAdjunto?: {
    nombre: string;
    url: string | null;
    duracionSeg?: number | null;
    eliminable?: boolean;
    /** Nota que el asesor escribió al subir esta versión (campo `descripcion` de la evidencia). */
    descripcion?: string | null;
  } | null;
  /** Posición (1-based) de la versión del archivo a la que pertenece este comentario, dentro
   * de la cadena de reemplazos (EVIDENCIA/AUDIO). Sin esto, un comentario viejo es ambiguo una
   * vez que hubo varias versiones — no se sabe a cuál se refería. */
  version?: number | null;
}

export type Actor = 'admin' | 'asesor' | 'sistema';

/**
 * Clave canónica única para agrupar historial por elemento: `${elementoTipo}:${elementoId}`.
 * Espejo del util equivalente en el backend (`tourixia-backend/src/modules/asesorias/revisionHistorial.util.ts`).
 * Fuente única de verdad para todos los `.get()`/`.set()` de mapas de historial en el frontend.
 */
export function historialKey(elementoTipo: string, elementoId: string | number | null): string {
  return `${elementoTipo}:${elementoId}`;
}

/** Agrupa el historial por elemento (tipo:id), con cada grupo ordenado cronológicamente.
 * Si se proporcionan cadenas de reemplazos de archivos (EVIDENCIA / AUDIO), unifica todas las versiones
 * de la cadena bajo la clave de la versión actual para consolidar la línea de tiempo. */
export function agruparHistorialPorElemento(
  historial: RevisionEntry[],
  cadenas?: { evidencias?: Array<Record<string, unknown>>; audios?: Array<Record<string, unknown>> }
): Map<string, RevisionEntry[]> {
  const grupos = new Map<string, RevisionEntry[]>();

  const raizMap = new Map<string, string>();
  if (cadenas?.evidencias) {
    for (const c of cadenas.evidencias) {
      const actual = c?.actual as { evidenciaId?: number | string } | undefined;
      const versions = Array.isArray(c?.version) ? (c.version as Array<{ evidenciaId?: number | string }>) : [];
      if (!actual || !versions.length) continue;
      const rootKey = `EVIDENCIA:${actual.evidenciaId}`;
      for (const v of versions) {
        if (v?.evidenciaId != null) raizMap.set(`EVIDENCIA:${v.evidenciaId}`, rootKey);
      }
    }
  }
  if (cadenas?.audios) {
    for (const c of cadenas.audios) {
      const actual = c?.actual as { audioId?: number | string } | undefined;
      const versions = Array.isArray(c?.version) ? (c.version as Array<{ audioId?: number | string }>) : [];
      if (!actual || !versions.length) continue;
      const rootKey = `AUDIO:${actual.audioId}`;
      for (const v of versions) {
        if (v?.audioId != null) raizMap.set(`AUDIO:${v.audioId}`, rootKey);
      }
    }
  }

  for (const h of historial) {
    const rawKey = historialKey(h.elementoTipo, h.elementoId);
    const key = raizMap.get(rawKey) ?? rawKey;
    if (!grupos.has(key)) grupos.set(key, []);
    grupos.get(key)!.push(h);
  }
  for (const arr of grupos.values()) {
    arr.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  }
  return grupos;
}

/** Devuelve las entradas de historial de un elemento concreto, ordenadas cronológicamente. */
export function historialDeElemento(historial: RevisionEntry[], elementoTipo: string, elementoId: string | number): RevisionEntry[] {
  const key = historialKey(elementoTipo, elementoId);
  return agruparHistorialPorElemento(historial).get(key) ?? [];
}

const ESTADOS_ASESOR = new Set(['RESPUESTA_ASESOR', 'CORREGIDO']);
const ESTADOS_ADMIN = new Set(['APROBADO', 'RECHAZADO', 'COMENTADO', 'DEVUELTA']);

/** Deriva el actor de una entrada de historial. Desconocido => 'sistema' (default seguro). */
export function actorDeEntry(entry: Pick<RevisionEntry, 'estado'>): Actor {
  if (entry.estado.startsWith('SISTEMA')) return 'sistema';
  if (ESTADOS_ASESOR.has(entry.estado)) return 'asesor';
  if (ESTADOS_ADMIN.has(entry.estado)) return 'admin';
  return 'sistema';
}

// Eventos OPEN reabren un ciclo de corrección; eventos RESOLVE lo cierran.
const OPEN_ESTADOS = new Set(['RECHAZADO', 'DEVUELTA', 'SISTEMA_REEMPLAZO_ELIMINADO']);
const RESOLVE_ESTADOS = new Set([
  'CORREGIDO',
  'RESPUESTA_ASESOR',
  'SISTEMA_EVIDENCIA_SUBIDA',
  'SISTEMA_AUDIO_VINCULADO',
  'SISTEMA_EVIDENCIA_ELIMINADA',
  'SISTEMA_AUDIO_ELIMINADO',
]);

/**
 * Predicado de resolución (espejo del backend). Las entradas no necesitan venir ordenadas.
 * Resuelto = no hay evento OPEN, o el último evento RESOLVE es estrictamente posterior
 * al último evento OPEN (empates NO cuentan como "posterior").
 */
export function estaResuelto(entries: RevisionEntry[]): boolean {
  if (entries.length === 0) return true;
  if (estaAprobado(entries)) return true;
  let lastOpen = -Infinity;
  let lastResolve = -Infinity;
  for (const e of entries) {
    const t = new Date(e.fecha).getTime();
    if (OPEN_ESTADOS.has(e.estado)) {
      if (t > lastOpen) lastOpen = t;
    } else if (RESOLVE_ESTADOS.has(e.estado)) {
      if (t > lastResolve) lastResolve = t;
    }
  }
  if (lastOpen === -Infinity) return true;
  return lastResolve > lastOpen;
}

/** Tope de comentarios (RESPUESTA_ASESOR) que el asesor puede escribir por ronda en un elemento. */
export const MAX_COMENTARIOS_RONDA = 3;

/**
 * Comentarios (RESPUESTA_ASESOR) que el asesor ya usó en la RONDA vigente del elemento. Una ronda
 * arranca en el último evento OPEN (RECHAZADO / DEVUELTA / SISTEMA_REEMPLAZO_ELIMINADO): cuando el
 * admin vuelve a rechazar, el conteo se reinicia. Las subidas de archivo NO cuentan (son ilimitadas).
 */
export function comentariosUsadosRonda(entries: RevisionEntry[]): number {
  let lastOpen = -Infinity;
  for (const e of entries) {
    if (OPEN_ESTADOS.has(e.estado)) {
      const t = new Date(e.fecha).getTime();
      if (t > lastOpen) lastOpen = t;
    }
  }
  return entries.filter((e) => e.estado === 'RESPUESTA_ASESOR' && new Date(e.fecha).getTime() > lastOpen).length;
}

/** Comentarios que le quedan al asesor en la ronda vigente del elemento (0..MAX_COMENTARIOS_RONDA). */
export function comentariosRestantesRonda(entries: RevisionEntry[]): number {
  return Math.max(0, MAX_COMENTARIOS_RONDA - comentariosUsadosRonda(entries));
}

export type EstadoConversacion = 'APROBADO' | 'RECHAZADO' | 'ESPERANDO_ADMIN' | 'ESPERANDO_ASESOR' | 'CORREGIDO' | 'SIN_ACTIVIDAD';

/**
 * Deriva el estado de la conversación a partir de la última entrada relevante.
 * Casi todos los eventos SISTEMA_* son puramente informativos y se ignoran, pero
 * SISTEMA_REEMPLAZO_ELIMINADO reabre el ciclo (espejo de `estaResuelto`/OPEN_ESTADOS):
 * el asesor deshizo su propia corrección y vuelve a estar en deuda, así que el turno
 * vuelve a ser suyo (ESPERANDO_ASESOR) en vez de quedar congelado en el último estado
 * previo a la subida del reemplazo.
 */
export function estadoConversacion(entries: RevisionEntry[]): EstadoConversacion {
  const relevantes = entries.filter((e) => !e.estado.startsWith('SISTEMA') || e.estado === 'SISTEMA_REEMPLAZO_ELIMINADO');
  if (relevantes.length === 0) return 'SIN_ACTIVIDAD';
  const sorted = [...relevantes].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const last = sorted[sorted.length - 1];
  switch (last.estado) {
    case 'APROBADO':
      return 'APROBADO';
    case 'RECHAZADO':
    case 'DEVUELTA':
    case 'COMENTADO':
    case 'SISTEMA_REEMPLAZO_ELIMINADO':
      return 'ESPERANDO_ASESOR';
    case 'RESPUESTA_ASESOR':
      return 'ESPERANDO_ADMIN';
    case 'CORREGIDO':
      return 'CORREGIDO';
    default:
      return 'SIN_ACTIVIDAD';
  }
}

function observacionVacia(observacion: string | null | undefined): boolean {
  return observacion === '' || observacion === null || observacion === undefined;
}

/**
 * Eventos de sistema de "rotación de archivo" (subir/eliminar reemplazo). Una corrección real
 * genera varios de estos seguidos (subió → eliminó → subió → ...); solo el ÚLTIMO de la corrida
 * refleja el estado neto (el archivo vigente o su ausencia), el resto es ruido.
 */
const RUIDO_SISTEMA_ROTACION = new Set([
  'SISTEMA_EVIDENCIA_SUBIDA',
  'SISTEMA_AUDIO_VINCULADO',
  'SISTEMA_REEMPLAZO_ELIMINADO',
  'SISTEMA_EVIDENCIA_ELIMINADA',
  'SISTEMA_AUDIO_ELIMINADO',
]);

/**
 * Colapsa, solo para presentación (no destructivo, no usar para lógica de negocio):
 *  - corridas contiguas de veredictos de admin con el MISMO estado y observación vacía, y
 *  - corridas contiguas de eventos de sistema de rotación de archivo (subió/eliminó/vinculó),
 * dejando en ambos casos la última entrada de la corrida (la más reciente / el estado neto).
 */
export function dedupeHistorialRuido(entries: RevisionEntry[]): RevisionEntry[] {
  const sorted = [...entries].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const result: RevisionEntry[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const actual = sorted[i];
    const siguiente = sorted[i + 1];
    const esCorridaAdmin =
      siguiente &&
      actorDeEntry(actual) === 'admin' &&
      actorDeEntry(siguiente) === 'admin' &&
      actual.estado === siguiente.estado &&
      observacionVacia(actual.observacion) &&
      observacionVacia(siguiente.observacion);
    const esCorridaSistema = !!siguiente && RUIDO_SISTEMA_ROTACION.has(actual.estado) && RUIDO_SISTEMA_ROTACION.has(siguiente.estado);
    if (esCorridaAdmin || esCorridaSistema) continue; // se descarta; la iteración siguiente conserva la última de la corrida
    result.push(actual);
  }
  return result;
}

/** True si el estado VIGENTE del elemento (su última entrada) es RECHAZADO. */
export function estaRechazadoVigente(entries: RevisionEntry[]): boolean {
  return !estaResuelto(entries);
}

/**
 * Elementos cuyo estado vigente es RECHAZADO (ignora rechazos ya resueltos/aprobados/apelados).
 * Retorna, por elemento, su hilo completo y la última entrada.
 */
export function elementosRechazadosVigentes(historial: RevisionEntry[]): Array<{ entries: RevisionEntry[]; ultimo: RevisionEntry }> {
  const out: Array<{ entries: RevisionEntry[]; ultimo: RevisionEntry }> = [];
  for (const entries of agruparHistorialPorElemento(historial).values()) {
    if (tieneRechazoActivo(entries)) out.push({ entries, ultimo: entries[entries.length - 1] });
  }
  return out;
}

/**
 * True si la última decisión del administrador (ignorando respuestas del asesor y eventos
 * SISTEMA_*) es RECHAZADO. Permite que un elemento siga siendo editable/corregible por el
 * asesor aunque ya haya enviado una respuesta. `SISTEMA_REEMPLAZO_ELIMINADO` es un evento OPEN
 * (reapertura), no una decisión de admin, por eso también se ignora aquí.
 */
export function tieneRechazoActivo(entries: RevisionEntry[]): boolean {
  if (entries.length === 0) return false;
  const sorted = [...entries].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const ultimoAdmin = [...sorted].reverse().find((e) => e.estado !== 'RESPUESTA_ASESOR' && !e.estado.startsWith('SISTEMA'));
  return ultimoAdmin?.estado === 'RECHAZADO';
}

/**
 * True si el asesor apeló (respondió al administrador) DESPUÉS del último rechazo.
 * Apelar es una forma válida de resolver un rechazo para poder reenviar a revisión,
 * aunque el rechazo del admin siga vigente hasta que este lo revise.
 */
export function fueApelado(entries: RevisionEntry[]): boolean {
  if (entries.length === 0) return false;
  const sorted = [...entries].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const idxUltimoRechazo = sorted.map((e) => e.estado).lastIndexOf('RECHAZADO');
  if (idxUltimoRechazo === -1) return false;
  return sorted.slice(idxUltimoRechazo + 1).some((e) => e.estado === 'RESPUESTA_ASESOR');
}

/**
 * True si la última entrada no-SISTEMA es APROBADO — el elemento está cerrado de verdad.
 * Espejo de `estaResuelto`/`estaAprobado` en el backend (`revisionHistorial.util.ts`).
 * A diferencia de `estaResuelto`, sí distingue "el asesor ya respondió, falta que el admin
 * revise" (no aprobado aún) de "el admin ya aprobó" (cerrado), porque `estaResuelto` no
 * rastrea APROBADO en absoluto.
 */
export function estaAprobado(entries: RevisionEntry[]): boolean {
  if (entries.length === 0) return false;
  const sorted = [...entries].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const ultimoNoSistema = [...sorted].reverse().find((e) => !e.estado.startsWith('SISTEMA'));
  return ultimoNoSistema?.estado === 'APROBADO';
}

/**
 * True si el elemento todavía necesita seguimiento del asesor o del admin — se mantiene
 * visible en la vista de correcciones mientras esto sea true, aunque el asesor ya haya
 * respondido (evita que el hilo desaparezca en cuanto se apela, antes de que el admin cierre
 * el ciclo con un APROBADO).
 */
export function necesitaSeguimiento(entries: RevisionEntry[]): boolean {
  return entries.length > 0 && !estaAprobado(entries);
}

/**
 * Tipos de elemento que el asesor efectivamente corrige. Excluye `ASESORIA` (eventos a nivel de
 * la asesoría, como DEVUELTA al devolverla a correcciones: es auditoría, no un ítem corregible).
 */
export const TIPOS_ELEMENTO_CORREGIBLE = new Set(['REQUISITO', 'OBSERVACIONES', 'PLANMEJORA', 'FECHAIMPLEMENTACION', 'EVIDENCIA', 'AUDIO']);

/**
 * Elementos corregibles cuyo ciclo sigue abierto (`estaResuelto` === false). Es el gate de
 * "Correcciones resueltas o apeladas" al reenviar a revisión. Ignora los eventos `ASESORIA`
 * (DEVUELTA es OPEN y, sin este filtro, bloquearía el envío para siempre pese a estar todo resuelto).
 */
export function elementosCorregiblesPendientes(
  historial: RevisionEntry[],
  cadenas?: { evidencias?: Array<Record<string, unknown>>; audios?: Array<Record<string, unknown>> }
): Array<{ elementoTipo: string; elementoId: string | null }> {
  const pendientes: Array<{ elementoTipo: string; elementoId: string | null }> = [];
  const grupos = agruparHistorialPorElemento(historial, cadenas);

  const cadenaResueltaMap = new Map<string, boolean>();

  if (cadenas?.evidencias) {
    for (const c of cadenas.evidencias) {
      const actual = c?.actual as { evidenciaId?: number | string; estado?: string; reemplazaEvidenciaId?: unknown } | undefined;
      const versions = Array.isArray(c?.version) ? (c.version as Array<unknown>) : [];
      if (!actual?.evidenciaId) continue;
      const rootKey = `EVIDENCIA:${actual.evidenciaId}`;
      const tieneReemplazo = versions.length > 1 || actual.reemplazaEvidenciaId != null;
      if (tieneReemplazo && actual.estado !== 'RECHAZADO') {
        cadenaResueltaMap.set(rootKey, true);
      }
    }
  }

  if (cadenas?.audios) {
    for (const c of cadenas.audios) {
      const actual = c?.actual as { audioId?: number | string; estado?: string; reemplazaAudioId?: unknown } | undefined;
      const versions = Array.isArray(c?.version) ? (c.version as Array<unknown>) : [];
      if (!actual?.audioId) continue;
      const rootKey = `AUDIO:${actual.audioId}`;
      const tieneReemplazo = versions.length > 1 || actual.reemplazaAudioId != null;
      if (tieneReemplazo && actual.estado !== 'RECHAZADO') {
        cadenaResueltaMap.set(rootKey, true);
      }
    }
  }

  const conocidosEvidencias = new Set<string>();
  if (cadenas?.evidencias) {
    for (const c of cadenas.evidencias) {
      const versions = Array.isArray(c?.version) ? (c.version as Array<{ evidenciaId?: unknown }>) : [];
      for (const v of versions) {
        if (v?.evidenciaId != null) conocidosEvidencias.add(String(v.evidenciaId));
      }
    }
  }

  const conocidosAudios = new Set<string>();
  if (cadenas?.audios) {
    for (const c of cadenas.audios) {
      const versions = Array.isArray(c?.version) ? (c.version as Array<{ audioId?: unknown }>) : [];
      for (const v of versions) {
        if (v?.audioId != null) conocidosAudios.add(String(v.audioId));
      }
    }
  }

  for (const [key, entries] of grupos.entries()) {
    const ultimo = entries[entries.length - 1]!;
    if (!TIPOS_ELEMENTO_CORREGIBLE.has(ultimo.elementoTipo)) continue;
    if (cadenaResueltaMap.get(key)) continue;

    const [tipoStr, rawIdStr] = key.split(':');
    const targetTipo = tipoStr || ultimo.elementoTipo;
    const targetId = rawIdStr || String(ultimo.elementoId);

    // Si el elemento es una EVIDENCIA o AUDIO y ya no existe físicamente en las listas activas,
    // es un registro huérfano en el historial y no debe bloquear la finalización.
    if (cadenas?.evidencias && targetTipo === 'EVIDENCIA') {
      if (!conocidosEvidencias.has(targetId)) continue;
    }
    if (cadenas?.audios && targetTipo === 'AUDIO') {
      if (!conocidosAudios.has(targetId)) continue;
    }

    if (estaResuelto(entries)) continue;
    pendientes.push({ elementoTipo: targetTipo, elementoId: targetId });
  }
  return pendientes;
}
